/**
 * 统一网络请求封装（fetch 版，沿用移动端的接口契约）
 *
 * 契约（与后端 / 移动端完全一致，改动前请先看这里）：
 *  1. 永远返回完整信封 { code, message, data }，code === 0 表示成功；
 *  2. 业务错误（code !== 0）不 reject，调用方直接判 res.code === 0 即可（不再额外解包）；
 *     只有网络层失败（断网 / 超时 / 解析失败）才 reject；
 *  3. 401 / 402 视为登录失效：清 token + 抛事件 + 跳登录页（1s 防抖）；
 *  4. 请求头：Content-Type: application/json（未显式指定时）、front-auth: <token>；
 *  5. GET 参数走 query，并追加 _t=时间戳 防缓存；
 *  6. 请求体里 undefined / null 字段会被删除（很多后端接口靠「不传」表示缺省）。
 */
import { APP_BASE_API } from '@/config/global'
import type { ApiResponse } from './interfaces'

/** token 存储键 */
export const TOKEN_KEY = 'accessToken'

/** token 变化监听器（主进程 / 多标签页无关，仅内存通知） */
const authListeners = new Set<() => void>()

export function getToken(): string {
  try {
    return localStorage.getItem(TOKEN_KEY) || ''
  } catch {
    return ''
  }
}

export function setToken(token: string) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token)
    else localStorage.removeItem(TOKEN_KEY)
  } catch {
    /* ignore */
  }
}

export function clearToken() {
  try {
    localStorage.removeItem(TOKEN_KEY)
  } catch {
    /* ignore */
  }
}

/** 订阅「登录失效」事件（对应移动端的 uni.$on('auth:logout')） */
export function onLogout(cb: () => void): () => void {
  authListeners.add(cb)
  return () => authListeners.delete(cb)
}

export interface RequestOptions {
  method: 'GET' | 'POST' | 'PUT' | 'DELETE'
  data?: any
  url: string
  headers?: Record<string, string>
  timeout?: number
}

export interface ExtraOptions {
  headers?: Record<string, string>
  timeout?: number
}

function isFormData(v: any): boolean {
  return typeof FormData !== 'undefined' && v instanceof FormData
}

/** 未登录 / 登录失效：清 token、通知 store、跳登录页（防抖 1s，避免并发请求重复跳转） */
let redirecting = false
function handleUnauthorized() {
  clearToken()
  authListeners.forEach((cb) => {
    try {
      cb()
    } catch {
      /* ignore */
    }
  })
  if (redirecting) return
  redirecting = true
  setTimeout(() => (redirecting = false), 1000)
  // 已不在登录页才跳，避免登录接口自己返回 401 时反复跳
  if (typeof location !== 'undefined' && !location.hash.includes('/login') && !location.pathname.includes('/login')) {
    emitNavigate('/login')
  }
}

/** 跳转事件：由 App.vue 的路由守卫接管，避免 api 层直接依赖 router */
let navigateHandler: ((url: string) => void) | null = null
export function setNavigateHandler(fn: (url: string) => void) {
  navigateHandler = fn
}
function emitNavigate(url: string) {
  if (navigateHandler) navigateHandler(url)
}

/** 统一 toast 钩子：由 App 注入，api 层不直接依赖 UI 组件 */
let toastHandler: ((msg: string) => void) | null = null
export function setToastHandler(fn: (msg: string) => void) {
  toastHandler = fn
}
export function toast(msg: string) {
  if (toastHandler) toastHandler(msg)
  else console.warn('[toast]', msg)
}

/** 过滤掉 undefined / null 参数（FormData 跳过） */
function cleanData(data: any) {
  if (!data || typeof data !== 'object' || isFormData(data)) return data
  const out: Record<string, any> = {}
  Object.keys(data).forEach((k) => {
    const v = (data as any)[k]
    if (v === undefined || v === null) return
    out[k] = v
  })
  return out
}

/** 拼接 query（跳过空值） */
function toQuery(data: any): string {
  if (!data || typeof data !== 'object') return ''
  const params = new URLSearchParams()
  Object.keys(data).forEach((k) => {
    const v = (data as any)[k]
    if (v === undefined || v === null || v === '') return
    params.append(k, String(v))
  })
  const s = params.toString()
  return s ? `?${s}` : ''
}

/** GET 防缓存时间戳 */
function withNoCache(url: string): string {
  return url + (url.includes('?') ? '&' : '?') + '_t=' + Date.now()
}

export interface RequestFn {
  <T = unknown>(options: RequestOptions): Promise<ApiResponse<T>>
  get<T>(url: string, data?: any, options?: ExtraOptions): Promise<ApiResponse<T>>
  post<T>(url: string, data?: any, options?: ExtraOptions): Promise<ApiResponse<T>>
  put<T>(url: string, data?: any, options?: ExtraOptions): Promise<ApiResponse<T>>
  del<T>(url: string, data?: any, options?: ExtraOptions): Promise<ApiResponse<T>>
}

const request: RequestFn = function <T = unknown>(
  options: RequestOptions = { method: 'GET', data: {}, url: '' }
): Promise<ApiResponse<T>> {
  const { method, url } = options
  const rawData = options.data ?? {}
  const data = cleanData(rawData)

  const headers: Record<string, string> = { ...(options.headers || {}) }
  const hasContentType = Object.keys(headers).some((k) => k.toLowerCase() === 'content-type')
  if (!hasContentType && !isFormData(data)) headers['Content-Type'] = 'application/json'
  const token = getToken()
  if (token) headers['front-auth'] = token

  let fullUrl = APP_BASE_API + url
  let body: any = undefined
  if (method === 'GET') {
    fullUrl = withNoCache(fullUrl + toQuery(data))
  } else if (isFormData(data)) {
    body = data
  } else {
    body = JSON.stringify(data)
  }

  const controller = new AbortController()
  const timer = options.timeout ? setTimeout(() => controller.abort(), options.timeout) : null

  return fetch(fullUrl, { method, headers, body, signal: controller.signal, cache: 'no-store' })
    .then(async (res) => {
      const text = await res.text()
      let raw: any = null
      try {
        raw = text ? JSON.parse(text) : null
      } catch {
        raw = null
      }
      const isObject = raw !== null && typeof raw === 'object'
      if (isObject && typeof raw.code === 'number') {
        const body2 = raw as ApiResponse<T>
        if (body2.code === 401 || body2.code === 402) handleUnauthorized()
        return body2
      }
      // 兜底：HTTP 失败 / 非标准响应（HTML 错误页、纯文本）
      const fallbackMsg =
        raw?.message ||
        raw?.msg ||
        (!res.ok ? `服务开小差了（${res.status}），请稍后再试` : '请求失败，请稍后再试')
      const fallbackCode = raw && raw.code !== undefined ? raw.code : res.ok ? -1 : res.status
      return { code: fallbackCode, message: fallbackMsg, data: raw?.data } as unknown as ApiResponse<T>
    })
    .catch((err: any) => {
      const aborted = err?.name === 'AbortError'
      throw new Error(aborted ? '请求超时，请稍后再试' : '网络异常，请检查网络连接')
    })
    .finally(() => {
      if (timer) clearTimeout(timer)
    })
} as RequestFn

request.get = (url, data, options) => request({ method: 'GET', url, data, headers: options?.headers, timeout: options?.timeout })
request.post = (url, data, options) => request({ method: 'POST', url, data, headers: options?.headers, timeout: options?.timeout })
request.put = (url, data, options) => request({ method: 'PUT', url, data, headers: options?.headers, timeout: options?.timeout })
request.del = (url, data, options) => request({ method: 'DELETE', url, data, headers: options?.headers, timeout: options?.timeout })

export default request
